<!-- Delivery table component -->
<template>
	<!-- eslint-disable no-mixed-spaces-and-tabs -->
	<ApiErrorAlert v-if="error" :error="error" />
	<div v-else-if="loading" class="m-auto text-center loading-state">
		<div class="text-primary spinner-border avatar-lg" role="status" />
	</div>
	<div v-else-if="list.length" style="display: flex; justify-content: space-around">
		<div :style="[expandedId !== 0 ? { width: '65%' } : { width: '100%' }]" class="card">
			<div class="card-body">
				<!-- <h4 class="mb-2 header-title">{{ $t('PORTAL.Deliveries.heading') }}</h4> -->
				<div class="mb-0 table-responsive">
					<template v-for="(item, index) of list">
						<div :key="`${index}_card`" class="card">
							<div class="card-body">
								<div class="order-title">
									<h4 :key="`${index}_order`">
										{{
											item.orderId
												? `ORDER REF ID: ${item.orderId}`
												: `PARCEL REF ID: ${item.clientRef.parcels[0]}`
										}}
									</h4>
									<h5 :key="`${index}_timeline_status`">
										<b-badge :class="statusColor(item.status)">
											{{
												$t(
													`PORTAL.Deliveries.STATUS.${item.status}`
												)
											}}
										</b-badge>
									</h5>
								</div>
								<div v-if="item.lastUpdatedTime && item.timezone" class="last-updated-time">
									{{
										$t(
											`PORTAL.Deliveries.TIMELINES.LAST_UPDATE_TIME`,
											{
												lastUpdateTime: displayDatetime(
													item.lastUpdatedTime,
													'YYYY-MM-DD HH:mm ([UTC]Z)',
													item.timezone
												)
											}
										)
									}}
								</div>
								<select v-if="item.orderId" @change.prevent="perFormAction">
									<option disabled selected value="MORE_ACTION">
										{{
											$t(
												'PORTAL.Deliveries.ACTIONS.MORE_ACTION'
											)
										}}
									</option>
									<option v-for="action in actions(
										item.clientId,
										item.status
									)" :key="action" :value="action">
										{{
											$t(
												`PORTAL.Deliveries.ACTIONS.${action}`
											)
										}}
									</option>
								</select>
								<order-events :events="item.orderEvents || []" :order-status="item.status" />
								<table :key="`${index}_deliveries`"
									class="mt-2 mb-0 table table-centered table-nowrap table-hover">
									<tbody>
										<template v-for="delivery of item.values">
											<tr :key="delivery.id" @click="toggleDetails(delivery)">
												<td style="width: 30%">
													<h5>
														PARCEL REF ID:
														{{
															delivery.clientRef
																.parcels[0]
														}}
													</h5>
												</td>
												<td style="
														min-width: 160px;
														max-width: 160px;
														white-space: normal;
													">
													<h5 class="font-weight-normal font-14 mb-1">
														<b-badge :class="statusColor(
															delivery.status
														)
															">
															{{
																$t(
																	`PORTAL.Deliveries.STATUS.${getDeliveryStatusTranslation(
																		delivery
																	)}`
																)
															}}
														</b-badge>
													</h5>
													<div class="font-13 text-muted">
														{{
															displayDatetime(
																delivery.updatedAt,
																'YYYY-MM-DD HH:mm ([UTC]Z)',
																delivery.timezone
															)
														}}
													</div>
													<div v-if="delivery.reason" class="font-13 text-muted">
														{{
															displayReason(
																delivery.reason,
																delivery.clientId
															)
														}}
													</div>
												</td>
												<td>
													<h5 v-if="delivery.hasSla"
														class="font-weight-normal font-13 text-muted mb-1">
														{{
															displayDatetime(
																delivery.deliveryBy,
																'YYYY-MM-DD HH:mm ([UTC]Z)',
																delivery.timezone
															)
														}}
													</h5>
													<h5 v-if="
														delivery.hasSla &&
														!delivery.order
															.isCancelled
													" :class="`font-14 mb-1 font-weight-normal ${slaDisplay(delivery)
														.late
														? 'text-danger'
														: ''
														}`">
														{{
															slaDisplay(delivery)
																.text
														}}
													</h5>
												</td>
												<td class="text-center table-action">
													<i :class="`action-icon mdi mdi-chevron-${delivery.id ===
														expandedId
														? 'up'
														: 'down'
														}`" />
												</td>
											</tr>
											<tr v-show="delivery.id === expandedId
												" :key="`${delivery.id}_collapsible`" class="special-table-row">
												<td colspan="3">
													<Activity :id="delivery.id" :key="delivery.id" :title="$t(
														'PORTAL.Deliveries.heading_logs'
													)
														" :activity-data="displayLogs(
															delivery
														)
															" :hide-close-btn="true" @click="
																toggleDetails(
																	delivery
																)
																" />
												</td>
											</tr>
										</template>
										<template v-if="!item.values">
											<tr :key="item.id" @click="toggleDetails(item)">
												<td style="width: 30%">
													<h5>
														PARCEL REF ID:
														{{
															item.clientRef
																.parcels[0]
														}}
													</h5>
												</td>
												<td style="
														min-width: 160px;
														max-width: 160px;
														white-space: normal;
													">
													<h5 class="font-weight-normal font-14 mb-1">
														<b-badge :class="statusColor(
															item.status
														)
															">
															{{
																$t(
																	`PORTAL.Deliveries.STATUS.${item.status}`
																)
															}}
														</b-badge>
													</h5>
													<div class="font-13 text-muted">
														{{
															displayDatetime(
																item.updatedAt,
																$t(
																	'PORTAL.Datetime.full'
																)
															)
														}}
													</div>
													<div v-if="item.reason" class="font-13 text-muted">
														{{
															displayReason(
																item.reason,
																item.clientId
															)
														}}
													</div>
												</td>
												<td>
													<h5 class="font-weight-normal font-14 mb-1">
														{{
															item.hasSla
																? displayDatetime(
																	item.deliveryBy,
																	$t(
																		'PORTAL.Datetime.time_24'
																	)
																)
																: $t(
																	'PORTAL.Deliveries.data_not_available'
																)
														}}
													</h5>
													<h5 v-if="item.hasSla"
														class="font-weight-normal font-13 text-muted mb-1">
														{{
															displayDatetime(
																item.deliveryBy,
																$t(
																	'PORTAL.Datetime.date_full'
																)
															)
														}}
													</h5>
													<h5 v-if="item.hasSla" :class="`font-14 mb-1 font-weight-normal ${slaDisplay(item)
														.late
														? 'text-danger'
														: ''
														}`">
														{{
															slaDisplay(item)
																.text
														}}
													</h5>
												</td>
												<td class="text-center table-action">
													<i :class="`action-icon mdi mdi-chevron-${item.id ===
														expandedId
														? 'up'
														: 'down'
														}`" />
												</td>
											</tr>
											<tr v-show="item.id === expandedId" :key="`${item.id}_collapsible`"
												class="special-table-row">
												<td colspan="3">
													<Activity :id="item.id" :key="item.id" :title="$t(
														'PORTAL.Deliveries.heading_logs'
													)
														" :activity-data="displayLogs(item)
															" :hide-close-btn="true" @click="
																toggleDetails(item)
																" />
												</td>
											</tr>
										</template>
									</tbody>
								</table>
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>
		<div v-if="expandedId" style="width: 30%" class="card">
			<div class="card-body">
				<div id="delivery-info">
					<div id="client-info">
						<span>
							{{ $t('PORTAL.Deliveries.heading_customer_info') }}
						</span>
						<a :href="isAdmOrOps
							? `${backofficeUrl}/#/clients/${expandedDelivery.clientId}`
							: 'javascript:void(0)'
							" style="font-size: 10px">
							ID: {{ expandedDelivery.clientId }}
						</a>
					</div>
					<div>
						{{ clientInfo }}
					</div>
					<hr />
					<div>
						<span>
							{{ $t('PORTAL.Deliveries.heading_sender_info') }}
						</span>
						<div>
							<span>
								{{ expandedDelivery.sender }} (Tel:
								<a href="javascript:void(0)">
									{{ expandedDelivery.senderPhone }}
								</a>
								)
							</span>
						</div>
						<br />
						<div>
							<span>{{ expandedDelivery.pickupAddress }}</span>
						</div>
					</div>
					<hr />
					<div>
						<span>
							{{ $t('PORTAL.Deliveries.heading_recipient_info') }}
						</span>
						<div>
							<span>
								{{ expandedDelivery.recipient }} (Tel:
								<a href="javascript:void(0)">
									{{ expandedDelivery.recipientPhone }}
								</a>
								)
							</span>
						</div>
						<div>
							<span>{{ expandedDelivery.dropoffAddress }}</span>
						</div>
						<div v-if="deliveryType">
							<hr />
							<span>
								{{
									$t(
										'PORTAL.Deliveries.heading_delivery_type'
									)
								}}
							</span>
							<br />
							<span>{{ deliveryType }}</span>
						</div>
						<hr />
						<div v-if="
							expandedDelivery.order &&
							expandedDelivery.order.shareLink
						">
							<span>
								{{
									$t('PORTAL.Deliveries.heading_tracking_url')
								}}
							</span>
							<br />
							<a :href="expandedDelivery.order.shareLink" target="_blank">
								{{ expandedDelivery.order.shareLink }}
							</a>
						</div>
					</div>
					<hr />
					<div v-if="expandedDelivery.order" class="extra-info-container">
						<div>
							<span class="extra-info-line-title">
								{{ $t('PORTAL.Place Order.label_select_delivery_stage') }}
							</span>
							<br />
							<span v-if="expandedDelivery.order.deliveryStage">
								{{
									expandedDelivery.order.deliveryStage
								}}
							</span>
							<span v-else>
								{{ t('PORTAL.Deliveries.data_not_available') }}
							</span>
						</div>
						<div v-if="expandedDelivery.order.parcelNum || expandedDelivery.order.totalWeight"
							class="extra-info-line-flex">
							<div>
								<span class="extra-info-line-title">
									{{ $t('PORTAL.Place Order.table_header_number_parcel') }}
								</span>
								<br />
								<span v-if="expandedDelivery.order.parcelNum && hasWeight">
									{{
										expandedDelivery.order.parcelNum
									}}
								</span>
								<span v-else>
									{{ $t('PORTAL.Deliveries.data_not_available') }}
								</span>
							</div>
							<div>
								<span class="extra-info-line-title">
									{{ $t('REPORT.EOD.weight') }}
								</span>
								<br />
								<span v-if="expandedDelivery.order.totalWeight && hasWeight">
									{{
										expandedDelivery.order.totalWeight
									}} kg
								</span>
								<span v-else>
									{{ $t('PORTAL.Deliveries.data_not_available') }}
								</span>
							</div>
						</div>
						<!-- Hidden for CSV order -->
						<div v-if="expandedDelivery.order.orderDisplayId">
							<span class="extra-info-line-title">
								{{ $t('PORTAL.Deliveries.heading_category') }}
							</span>
							<br />
							<span v-if="expandedDelivery.order.notes[0].categories.length > 0">
								{{ expandedDelivery.order.notes[0].categories.join(',\n') }}
							</span>
							<span v-else>
								{{ $t('PORTAL.Deliveries.data_not_available') }}
							</span>
						</div>
						<!-- Hidden for CSV order -->
						<div v-if="expandedDelivery.order.orderDisplayId">
							<span class="extra-info-line-title">
								{{ $t('PORTAL.Deliveries.heading_handling_instructions') }}
							</span>
							<br />
							<span v-if="expandedDelivery.order.notes[0].handlingInstructions.length > 0">
								{{ expandedDelivery.order.notes[0].handlingInstructions.join(',\n') }}
							</span>
							<span v-else>
								{{ $t('PORTAL.Deliveries.data_not_available') }}
							</span>
						</div>
						<div class="extra-info-line-flex">
							<div>
								<span class="extra-info-line-title">
									{{ $t('PORTAL.Deliveries.heading_payment_type') }}
								</span>
								<br />
								<span v-if="expandedDelivery.order.paymentType">
									{{
										expandedDelivery.order.paymentType
									}}
								</span>
								<span v-else>
									{{ $t('PORTAL.Deliveries.data_not_available') }}
								</span>
							</div>
							<div>
								<span class="extra-info-line-title">
									{{ $t('PORTAL.Deliveries.heading_payment_amount') }}
								</span>
								<br />
								<span v-if="formattedPaymentAmount">
									{{ formattedPaymentAmount }}
									{{ currentCityCurrency }}
								</span>
								<span v-else>
									{{ $t('PORTAL.Deliveries.data_not_available') }}
								</span>
							</div>
						</div>
						<!-- Different handling for CSV order and API order as their api res structure not the same... -->
						<div v-if="expandedDelivery.order.orderDisplayId">
							<span class="extra-info-line-title">
								{{ $t('PORTAL.Deliveries.heading_metadata') }}
							</span>
							<br />
							<span v-if="hasMetadata">
								<div v-for="(value, key) in expandedDelivery.order.extras[0]" :key="key">
									<span>{{ key }}: {{ value }}</span>
								</div>
							</span>
							<span v-else>
								{{ $t('PORTAL.Deliveries.data_not_available') }}
							</span>
						</div>
						<div v-else>
							<span class="extra-info-line-title">
								{{ $t('PORTAL.Deliveries.heading_metadata') }}
							</span>
							<br />
							<span v-if="expandedDelivery.order.extras.length > 0">
								{{ JSON.stringify(expandedDelivery.order.extras[0]) }}
							</span>
							<span v-else>
								{{ $t('PORTAL.Deliveries.data_not_available') }}
							</span>
						</div>
						<!-- Different handling for CSV order and API order as their api res structure not the same... -->
						<div v-if="expandedDelivery.order.orderDisplayId">
							<span class="extra-info-line-title">
								{{ $t('Summary.label_remarks') }}
							</span>
							<br />
							<span v-if="expandedDelivery.order.address && concatenatedRemarks">
								{{ concatenatedRemarks }}
							</span>
							<span v-else>
								{{ $t('PORTAL.Deliveries.data_not_available') }}
							</span>
						</div>
						<div v-else>
							<span class="extra-info-line-title">
								{{ $t('Summary.label_remarks') }}
							</span>
							<br />
							<span v-if="expandedDelivery.order.notes.length > 0">
								{{ expandedDelivery.order.notes.join(' || ') }}
							</span>
							<span v-else>
								{{ $t('PORTAL.Deliveries.data_not_available') }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-else class="empty-placeholder">
			<a v-if="!currentId" class="cursor-pointer text-center" @click="focusSearch">
				<p>
					<img src="@/assets/images/file-searching-llm.svg" height="120" alt="image" />
				</p>
				<p>{{ $t('PORTAL.Deliveries.table_placeholder_no_record') }}</p>
			</a>
			<div v-else class="text-center">
				<p>
					<img src="@/assets/images/table_no_result.svg" height="120" alt="image" />
				</p>
				<p>
					{{
						$t('PORTAL.Deliveries.table_placeholder_no_result', {
							id: currentId
						})
					}}
				</p>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment'
import ApiErrorAlert from '@/components/ApiErrorAlert.vue'
import Activity from '@/components/widgets/Activity.vue'
import router from '@/router'
import {
	deliveriesComputed,
	deliveriesMethods,
	configComputed,
	configMethods
} from '@/store/helpers'
import { displayDatetime, getTimezone } from '@/utils/helpers'
import axiosApiClient from '@/api/axiosApiClient'
import {
	DELIVERY_STATUS,
	DELIVERY_STATUS_GROUP,
	ORDER_STATUS
} from '@/utils/constants'
import OrderEvents from './OrderEvents.vue'
import _ from 'lodash'
import { ORDER_STATUS_GROUP } from '../utils/constants'
import { getSavedState } from '@/store/helpers'

const { VUE_APP_SG_BACKOFFICE_URL, VUE_APP_BR_BACKOFFICE_URL } = process.env

export default {
	components: { Activity, ApiErrorAlert, OrderEvents },
	props: {
		user: {
			type: Object,
			required: false,
			default: () => null
		}
	},
	data() {
		const isAdmOrOps = this.user.roles.some(i => ['ADM', 'OPS'].includes(i))
		return {
			expandedId: 0,
			expandedDelivery: {},
			deliveries: [],
			action: 'MORE_ACTION',
			backofficeUrl:
				getSavedState('config.dc') === 'SIN' ||
					!getSavedState('config.dc')
					? VUE_APP_SG_BACKOFFICE_URL
					: VUE_APP_BR_BACKOFFICE_URL,
			isAdmOrOps
		}
	},
	computed: {
		...deliveriesComputed,
		...configComputed,
		clientInfo() {
			const { locale, clients } = this.$store.state.config
			const clientId = this.expandedDelivery.clientId
			const client = clients.find(c => c.id === clientId)
			if (!client) {
				return
			}
			const name = `${(
				client.translations.find(t => t.locale === locale) ||
				client.translations[0]
			).name
				}`
			const location = (
				this.expandedDelivery.formSubmissions[0] ||
				this.expandedDelivery.order
			).locationId
			return `${name} - ${location}`
		},
		deliveryType() {
			return _.get(
				this.expandedDelivery,
				'order.extras.0.deliveryType',
				''
			)
		},
		hasMetadata() {
			return (
				this.expandedDelivery.order.extras &&
				Object.keys(this.expandedDelivery.order.extras[0]).length > 0
			)
		},
		currentCityCurrency() {
			const locationEngShorthand = this.expandedDelivery.order.locationId?.split('_')[0] // extract HK from HK_HKG
			const city = this.$store.state.config.regions.find(
				region => region.id === locationEngShorthand
			)

			return city?.currency.standard_unit || ''
		},
		hasWeight() {
			return this.expandedDelivery.order.totalWeight !== "0.00"
		},
		formattedPaymentAmount() {
			let amount = this.expandedDelivery.order.paymentAmount
			if (typeof amount === 'string') {
				amount = parseFloat(amount)
			}
			return amount ? amount.toFixed(2) : null
		},
		concatenatedRemarks() {
			const addresses = this.expandedDelivery.order.address || []
			const senderRemarks = addresses[0]?.remarks || ''
			const recipientRemarks = addresses[1]?.remarks || ''

			if (senderRemarks && recipientRemarks) {
				return `${senderRemarks} || ${recipientRemarks}`
			} else if (senderRemarks) {
				return `${senderRemarks} ||`
			} else if (recipientRemarks) {
				return `|| ${recipientRemarks}`
			} else {
				return ''
			}
		}
	},
	watch: {
		list: function (newList) {
			this.expandedId = 0
			const deliveryRefs = []
			newList.forEach(i => {
				const { orderId, values } = i
				if (orderId) {
					deliveryRefs.push(...values)
					return
				}
				deliveryRefs.push(i)
			})

			this.deliveries = deliveryRefs
			const clientRef =
				this.$route.params.clientRef || this.$route.query.search
			if (clientRef) {
				// If there is a clientRef from path parameter:
				// Find the delivery item with that clientRef, and expand it:
				const item = this.findDeliveryByClientRef(
					deliveryRefs,
					clientRef
				)
				if (item) {
					this.expandedId = item.id
					this.expandedDelivery = item
				}
			} else {
				// If there is no clientRef path param, but only 1 item in the list:
				let clientRefVal
				if (this.expandedId) {
					// Get its clientRef value:
					clientRefVal = this.getDeliveryClientRef(deliveryRefs[0])
				}
				// Update path parameter in URL:
				router
					.push({
						name: 'Deliveries',
						params: { clientRef: clientRefVal },
						query: this.$route.query
					})
					.catch(err => { })
			}
		},
		'$route.params.clientRef': function (clientRef) {
			this.expandedId = 0
			// If clientRef from path parameter changes:
			if (clientRef) {
				// Find the delivery item with that clientRef, and expand it:
				const item = this.findDeliveryByClientRef(
					this.deliveries,
					clientRef
				)
				if (item && this.expandedId !== item.id) {
					this.expandedId = item.id
				}
			}
		}
	},
	mounted() {
		if (Object.keys(this.reasons).length === 0) {
			this.fetchClients()
			this.fetchReasons()
		}
	},
	methods: {
		...deliveriesMethods,
		...configMethods,
		displayDatetime,
		actions(clientId, orderStatus) {
			if (ORDER_STATUS_GROUP.END.includes(orderStatus)) {
				return []
			}
			const { clients } = this.$store.state.config
			const client = clients.find(c => c.id === clientId)
			// ADM OR OPS can cancel or delete order regardless of client config
			return this.isAdmOrOps || (client && client.canCancelOrders)
				? ['CANCEL']
				: []
		},
		// Maps delivery status to badge color:
		statusColor(deliveryOrOrderStatus) {
			const has = statues => status => statues.includes(status)
			return _.cond([
				[
					has([
						DELIVERY_STATUS.FAILED,
						DELIVERY_STATUS.FAILED_PICKUP
					]),
					_.constant('badge-outline-danger')
				],
				[
					has([
						DELIVERY_STATUS.RETURNED,
						ORDER_STATUS.CANCELED,
						ORDER_STATUS.COMPLETED,
						ORDER_STATUS.CLOSED
					]),
					_.constant('badge-outline-warning')
				],
				[
					has([
						DELIVERY_STATUS.DELIVERED,
						DELIVERY_STATUS.RESCHEDULED,
						ORDER_STATUS.DELIVERED
					]),
					_.constant('badge-outline-success')
				],
				[
					has([
						DELIVERY_STATUS.DELIVERING,
						DELIVERY_STATUS.IN_TRANSIT
					]),
					_.constant('badge-outline-info-dark')
				],
				[
					has([DELIVERY_STATUS.AWAITS_DRIVER]),
					_.constant('badge-outline-secondary')
				],
				[
					has([DELIVERY_STATUS.AWAITS_PICKUP]),
					_.constant('badge-outline-info')
				],
				[
					has([
						ORDER_STATUS.PARTIAL_DELIVERED,
						ORDER_STATUS.IN_PROGRESS
					]),
					_.constant('')
				],
				[_.stubTrue, _.constant('badge-outline-secondary')]
			])(deliveryOrOrderStatus)
		},
		// Display client refs:
		displayClientRefs(clientRefs) {
			return clientRefs.join(', ')
		},
		// Translate time difference in minutes to human readable relative time:
		parseDiff(diffMin) {
			let minutes = Math.abs(diffMin)
			let hours = Math.trunc(minutes / 60)
			minutes = minutes % 60
			const days = Math.trunc(hours / 24)
			hours = hours % 24
			const daysText = days
				? this.$t('PORTAL.Datetime.diff_day', { count: days })
				: ''
			const hoursText = hours
				? this.$t('PORTAL.Datetime.diff_hr', { count: hours })
				: ''
			const minutesText = minutes
				? this.$t('PORTAL.Datetime.diff_min', { count: minutes })
				: ''
			return (
				`${daysText}${hoursText}${minutesText}` ||
				this.$t('PORTAL.Datetime.diff_now')
			)
		},
		slaDisplay(rec) {
			const sla = moment.utc(rec.deliveryBy)
			if (
				[
					...DELIVERY_STATUS_GROUP.PENDING,
					...DELIVERY_STATUS_GROUP.INTERIM,
					...DELIVERY_STATUS_GROUP.IN_TRANSIT
				].includes(rec.status)
			) {
				const diffMin =
					Math.trunc(sla.unix() / 60) -
					Math.trunc(moment.utc().unix() / 60)
				const diffText = this.parseDiff(diffMin)
				if (diffMin > 0) {
					return {
						text: this.$t('PORTAL.Deliveries.SLA.ongoing_before', {
							diffText
						}),
						late: diffMin < 60
					}
				}
				if (diffMin < 0) {
					return {
						text: this.$t('PORTAL.Deliveries.SLA.ongoing_after', {
							diffText
						}),
						late: true
					}
				}
				return {
					text: this.$t('PORTAL.Deliveries.SLA.ongoing_exact'),
					late: true
				}
			}

			if (DELIVERY_STATUS_GROUP.END.includes(rec.status)) {
				const diffMin =
					Math.trunc(sla.unix() / 60) -
					Math.trunc(moment.utc(rec.updatedAt).unix() / 60)
				const diffText = this.parseDiff(diffMin)
				if (diffMin > 0) {
					return {
						text: this.$t(
							'PORTAL.Deliveries.SLA.completed_before',
							{
								diffText
							}
						),
						late: false
					}
				}
				if (diffMin < 0) {
					return {
						text: this.$t('PORTAL.Deliveries.SLA.completed_after', {
							diffText
						}),
						late: true
					}
				}
				return {
					text: this.$t('PORTAL.Deliveries.SLA.completed_exact'),
					late: false
				}
			}

			if ([DELIVERY_STATUS.CANCELED].includes(rec.status)) {
				return { text: '', late: false }
			}
		},
		toggleDetails(item) {
			this.expandedId = this.expandedId !== item.id ? item.id : 0
			let clientRef
			if (this.expandedId) {
				// If a delivery is clicked open:
				const item = this.deliveries.find(i => i.id === this.expandedId)
				if (item) {
					// Get its clientRef value:
					clientRef = this.getDeliveryClientRef(item)
					this.expandedDelivery = item
				}
			}
			let query = this.$route.query
			if (clientRef) {
				query = { search: clientRef }
			}
			// Update path parameter in URL:
			router.push({ name: 'Deliveries', params: { clientRef }, query })
		},
		// Display delivery logs:
		displayLogs(delivery) {
			// const proofsOfDelivery = _.chain(item.formSubmissions, fs => ["PHOTO", "SIGNATURE"].includes(fs.))
			const activityLogs = delivery.logs
				.filter(log => !log.hidden) // Filter out hidden items (e.g. cross-dock REVIEWING/REJECTED).
				.map((log, i) => {
					let timezone
					if (log.tag == 'ORDER') {
						timezone =
							delivery.timezone ||
							getTimezone(
								this.locations,
								delivery.formSubmissions[i].locationId
							)
						// if no timezone info then use locationId in form submission instead
					} else {
						const formSubmissionIndex = _.findIndex(
							delivery.logs.filter(i => i.tag !== 'ORDER'),
							log
						)
						timezone = getTimezone(
							this.locations,
							delivery.formSubmissions[
								Math.max(formSubmissionIndex, 0)
							].locationId
						)
					}
					return {
						...log,
						title: this.$t(
							`PORTAL.Deliveries.LOG_TITLE.${log.tag}`
						),
						...(log.reason && {
							subtitle: this.displayReason(
								log.reason,
								log.clientId
							)
						}),
						text: this.displayDatetime(
							log.datetime,
							'YYYY-MM-DD HH:mm ([UTC]Z)',
							timezone
						),
						...(log.courierName && {
							subtext: `${this.$t(
								'PORTAL.Deliveries.table_header_courier'
							)}:`,
							boldSubtext: `${log.courierName + ' ' + log.courierPhone
								}`
						}),
						...(log.remark && {
							remarkText: `${this.$t(
								'PORTAL.Deliveries.table_header_courier_remark'
							)}: ${log.remark}`
						}),
						icon: 'mdi-check-bold',
						color: 'primary',
						backgroundColor: 'primary'
					}
				})
			// If Delivery status is not final, render a placeholder item:
			if (!DELIVERY_STATUS_GROUP.END.includes(delivery.status)) {
				if (activityLogs.length) {
					activityLogs[activityLogs.length - 1].backgroundColor =
						'gray'
				}
				activityLogs.push({
					id: 'placeholder',
					title: '\u200B', // zero width space
					color: 'secondary',
					backgroundColor: 'gray'
				})
			}
			return activityLogs
		},
		// Finds failure/success reason translation for current locale by key.
		// Fallbacks to key.
		displayReason(key, clientId) {
			// If client specific reason not found try with COMMON
			if (!this.reasons[clientId] || !this.reasons[clientId][key]) {
				clientId = 'COMMON'
				if (!this.reasons[clientId] || !this.reasons[clientId][key]) {
					return key
				}
			}
			const found = this.reasons[clientId][key].find(
				t => t.locale === this.locale
			)
			return found ? found.value : key
		},
		// Put focus on the main search input:
		focusSearch() {
			const searchInput = document.getElementById('input-primary-search')
			searchInput.focus()

			const searchLink = document
				.getElementById('item-primary-search-mobile')
				.getElementsByTagName('a')[0]
			searchLink.click()
		},
		findDeliveryByClientRef(list, clientRef) {
			return list.find(i => i.clientRefs.some(c => c.value === clientRef))
		},
		getDeliveryClientRef(delivery) {
			// Get clientRef of type PARCEL first, if no PARCEL, get the first one:
			const ref =
				delivery.clientRefs.find(c => c.type === 'PARCEL') ||
				delivery.clientRefs[0]
			return ref.value
		},
		async perFormAction() {
			const { expandedDelivery } = this
			const orderId = expandedDelivery.order.id
			const clientRef = expandedDelivery.clientRef.parcels[0]
			await axiosApiClient.patch(`orders/${orderId}`, {
				status: ORDER_STATUS.CANCELED
			})
			await this.fetchDeliveries({ clientRef })
		},
		getDeliveryStatusTranslation(item) {
			const deliveryState = _.get(item, 'order.deliveryStage')
			const deliveryStatus = _.get(item, 'status')
			if (deliveryStatus === DELIVERY_STATUS.DELIVERED && deliveryState) {
				return `${deliveryState}_${deliveryStatus}`
			}
			return deliveryStatus
		}
	}
}
</script>

<style scoped lang="scss">
tr.special-table-row:hover {
	background-color: white !important;
}

tr.special-table-row td {
	border-top: 0;
}

.badge {
	font-size: 1em;
}

.badge-outline-info-dark {
	border: 1px solid #2c8ef8;
	color: #2c8ef8;
	background: none;
}

.badge-outline-warning-dark {
	border: 1px solid #fc981c;
	color: #fc981c;
	background: none;
}

.last-updated-time {
	margin-top: 10px;
}

div.empty-placeholder,
div.loading-state {
	width: 20rem;
	height: 20rem;
	margin: auto;
	display: flex;
	flex-direction: column;
	justify-content: center;
}

select {
	color: #6c757d;
	border: 0px;
	outline: 0px;
	margin-top: 10px;
	margin-left: -5px;
}

.order-title {
	display: flex;
	align-items: center;

	h4,
	h5 {
		margin: 0;
	}

	h5 {
		margin-left: 10px;
	}
}

.extra-info-container {
	display: flex;
	flex-direction: column;
	row-gap: .5rem;
}

.extra-info-line-flex {
	display: grid;
	column-gap: .75rem;
	grid-template-columns: repeat(2, minmax(50%, 1fr));
}

.extra-info-line-title {
	font-weight: bold;
}
</style>
